import { ModelConceptRelationship } from 'src/containers/ProjectDiagram/types/data';
import { LinkData } from 'src/containers/ProjectDiagram/types/model';
import { ClassHierarchy } from 'src/containers/ProjectDiagram/utils/getClassHierarchy';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { getConnectedRelationshipDataForClass } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/getConnectedRelationshipDataForClass';
import { getRelationshipDomainsAndRanges } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/getRelationshipDomainsAndRanges';

export type RelationshipWithHierarchyData = {
  depth: number; // for now, mainly used to determine if inherited/not inherited
  domains: Set<string>;
  iri: string;
  label: string;
  ranges: Set<string>;
  sourceClassIris: Set<string>; // classes from same depth of ancestry
};

export const getAllInheritedRelationships = ({
  classIri,
  hierarchyByIri,
  labelsByIri,
  linkDataArray,
}: {
  classIri?: string;
  hierarchyByIri: Record<string, ClassHierarchy>;
  labelsByIri: Record<string, string>;
  linkDataArray: LinkData[];
}): Record<string, RelationshipWithHierarchyData> => {
  if (!classIri || !hierarchyByIri[classIri]) {
    return {};
  }

  const minDepthByVisitedClass: Record<string, number> = {};

  const relationshipsByIri: Record<string, RelationshipWithHierarchyData> = {};

  const addRelationship =
    (currentIri: string, depth: number) =>
    (relationship: ModelConceptRelationship) => {
      // add the found relationship to the results, or replace existing
      // results if the relationship is inherited from a closer parent
      if (
        !relationshipsByIri[relationship.value] ||
        depth < relationshipsByIri[relationship.value].depth
      ) {
        relationshipsByIri[relationship.value] = {
          depth,
          domains: new Set(),
          iri: relationship.value,
          label: getLabelForIri(labelsByIri, relationship.value),
          ranges: new Set(),
          sourceClassIris: new Set([currentIri]),
        };
      }

      // append the parent to this relationship if it shares the same
      // depth as other recognized parents
      else if (depth === relationshipsByIri[relationship.value].depth) {
        relationshipsByIri[relationship.value].sourceClassIris.add(currentIri);
      }
    };

  const relationshipsForClass = getConnectedRelationshipDataForClass(
    { linkDataArray },
    classIri
  );
  relationshipsForClass.forEach(addRelationship(classIri, 1));

  Object.entries(hierarchyByIri[classIri].ancestors).forEach(
    ([ancestor, depth]) => {
      const minDepth = minDepthByVisitedClass[ancestor];
      if (minDepth && minDepth <= depth) {
        return;
      }

      const relationshipsForAncestor = getConnectedRelationshipDataForClass(
        { linkDataArray },
        ancestor
      );

      relationshipsForAncestor.forEach(addRelationship(ancestor, depth));
    }
  );

  Object.values(relationshipsByIri).forEach((relationship) => {
    const { domains, ranges } = getRelationshipDomainsAndRanges(
      linkDataArray,
      relationship.iri
    );
    domains.forEach((domain) => {
      relationship.domains.add(domain);
      Object.keys(hierarchyByIri[domain]?.descendants).forEach(
        (descendantIri) => {
          relationship.domains.add(descendantIri);
        }
      );
    });
    ranges.forEach((range) => {
      relationship.ranges.add(range);
      Object.keys(hierarchyByIri[range]?.descendants).forEach(
        (descendantIri) => {
          relationship.ranges.add(descendantIri);
        }
      );
    });
  });

  return relationshipsByIri;
};
